<script>
import { USER_ACTION, DOC_TYPES, PROOF_VIEW_MODES } from "../../utils/constants";
import { getDefaultThumbnail } from "../../utils/helpers";

const { APPROVE, REJECT } = USER_ACTION;
const { IMAGE } = DOC_TYPES;
const { APPROVER_MODE } = PROOF_VIEW_MODES;

export default {
  name: 'files-to-review-screen',
  data() {
    return {
      APPROVE,
      REJECT,
      APPROVER_MODE,
    };
  },
  props: {
    files: {
      type: Array,
      required: true
    },
    currentProofVersion: {
      type: Number,
      required: true
    },
    allowApproveIndividualFiles: {
      type: Boolean,
      default: false
    },
    isCompareMode: {
      required: true
    },
    mode: {
      type: Number
    },
    approverFeedback: {
      type: Object
    },
    isViewingPreviousVersion: {
      type: Boolean,
      default: false
    },
  },
  methods: {
    getThumbnailUrl(path) {
      if (path.startsWith('static')) {
        return '/' + path;
      }
      if (path.startsWith('public/icon/')) {
        return path.replace('public/icon/', '/img/');
      }
      return path;
    },
    viewFile(file) {
      this.$emit('view-file', file);
    },
    approveFile(file) {
      this.$emit('approve-file', file);
    },
    rejectFile(file) {
      this.$emit('reject-file', file);
    },
    getLabel: function (section, key) {
      return this.$A.LangService.getLabel(section, key);
    },
    getDefaultThumbnail
  },
  computed: {
    showActions() {
      return this.approverFeedback === null;
    },
  },
};
</script>

<template>
  <div class="bg-white">
    <div class="container-fluid px-4 py-3 bg-white">
      <h3 class="files-heading mb-4 ff-montserrat">Files to Review</h3>
      <div class="row">
        <div v-for="file in files" :key="file.id" class="card-container mb-4" :class="isCompareMode ? 'col-xl-6' : 'col-sm-6 col-xl-3'">
          <div class="card h-100 bg-white">
            <div>
              <div class="position-relative image-container">
                <div 
                  class="bg-light w-100 h-100 d-flex justify-content-center align-items-center image-card clickable-thumbnail"
                  @click="viewFile(file)"
                  :title="`Click to view ${file.name || 'file'}`"
                >
                  <div class="w-100 h-100">
                    <img
                      :src="file.thumbnailFileKey ? getThumbnailUrl(file.thumbnailFileKey) : getDefaultThumbnail(file.docTypeGroup)"
                      alt="Thumbnail"
                      class="image-thumbnail"
                    />
                  </div>
                  <span v-if="file.proofFileAnnotations.length > 0" class="version-show-bottom-right">
                    {{ file.proofFileAnnotations.length }} 
                  </span>
                </div>
                <span v-if="!file.viewed" class="position-absolute top-0 start-0 m-2 not-viewed-badge fs-12">
                  Not Viewed
                </span>
              </div>
            </div>
            <div class="card-body text-center d-flex flex-column justify-content-between pt-3 pb-4 px-4">
              <p class="card-text file-name mb-2">{{ file.name || 'Unnamed File' }}</p>
              <button class="view-btn border-gray-300 btn border text-gray-700 mt-auto w-100 fs-14"
                @click="viewFile(file)">
                View
              </button>
              <div v-if="mode === APPROVER_MODE && allowApproveIndividualFiles && !isViewingPreviousVersion"
                :class="{
                  'd-flex': showActions,
                  'd-none': !showActions
                }"
                class="mt-2 justify-content-between align-items-center w-100 status-buttons">
                <div
                  class="action-button pt-2 pb-2"
                  :class="{ approve: file.userAction === APPROVE, reject: file.userAction === REJECT }"
                  @click="approveFile(file)"
                >
                  <div class="d-flex align-items-center justify-content-center px-1">
                    <img
                      :src="file.userAction === APPROVE ? '/img/thumbsFillUp.svg' : '/img/thumbsUp.svg'"
                      alt="approve-icon"
                      class="mr-2"
                      width="20"
                      height="20"
                    />
                    <span
                      v-show="file.userAction !== REJECT"
                      class="action-label"
                      :class="{ approved: file.userAction === APPROVE }"
                    >
                      {{ getLabel('review_screens_labels', 'approve_btn') }}
                    </span>
                  </div>
                </div>
                <div
                  class="status-divider"
                  :class="file.userAction === APPROVE ? 'left-75' : file.userAction === REJECT ? 'left-25' : 'left-50'"
                ></div>
                <div
                  class="action-button pt-2 pb-2"
                  :class="{ approve: file.userAction === APPROVE, reject: file.userAction === REJECT }"
                  @click="rejectFile(file)"
                >
                  <div class="d-flex align-items-center justify-content-center px-1">
                    <img
                      :src="file.userAction === REJECT ? '/img/thumbsFillDown.svg' : '/img/thumbsDown.svg'"
                      alt="reject-icon"
                      class="mr-2"
                      width="20"
                      height="20"
                    />
                    <span
                      v-show="file.userAction !== APPROVE"
                      class="action-label"
                      :class="{ rejected: file.userAction === REJECT }"
                    >
                      {{ getLabel('review_screens_labels', 'not_approved_btn') }}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.files-heading {
  font-weight: 700;
  font-size: 28px;
  line-height: 34px;
  color: #101828;
  letter-spacing: 0px;
}

.file-name {
  font-family: 'Open Sans', sans-serif;
  font-weight: 400;
  font-size: 16px;
  line-height: 24px;
  color: #374151;
  letter-spacing: 0px;
  overflow: hidden;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  text-overflow: ellipsis;
}

.view-btn {
  font-family: 'Open Sans', sans-serif;
  font-weight: 600;
  background-color: white;
  transition: all 0.2s ease;
  border-radius: 8px;
}

.view-btn:hover {
  background-color: #F3F4F6;
  border-color: #9CA3AF;
}

.not-viewed-badge {
  background-color: #FEF3C7;
  color: #D97706;
  padding: 4px 8px;
  border-radius: 12px;
  font-weight: 600;
  white-space: nowrap;
}

.version-show-bottom-right {
  position: absolute;
  bottom: 8px;
  right: 8px;
  background-color: #D1F6D4;
  color: #264C29;
  padding: 2px 6.5px;
  border-radius: 10px;
  font-size: 12px;
  z-index: 2;
}

.image-container {
  height: 224px;
  margin: 24px 24px;
  margin-bottom: 0px;
}

.image-card {
  border: 2px solid #448BE2;
  border-radius: 8px;
}

.clickable-thumbnail {
  cursor: pointer;
  transition: all 0.2s ease;
  user-select: none;
}

.clickable-thumbnail:hover {
  transform: scale(1.02);
  box-shadow: 0 8px 25px rgba(68, 139, 226, 0.15);
  border-color: #2563EB;
}

.clickable-thumbnail:active {
  transform: scale(0.98);
}

.status-buttons {
  font-family: 'Open Sans', sans-serif;
  font-size: 14px;
  line-height: 20px;
  letter-spacing: 0%;
  height: auto;
}

.image-thumbnail {
  width: 100%;
  height: 100%;
  object-fit: contain;
  object-position: center;
  pointer-events: none; /* Prevent image from interfering with parent click */
}

.action-button {
  cursor: pointer;
  flex: 1;
  transition: transform 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
}

.action-button.approve {
  transform: translateX(40%);
}

.action-button.reject {
  transform: translateX(-40%);
}

.action-label {
  color: #000;
  font-weight: 400;
}

.action-label.approved {
  font-weight: 600;
  background-image: none !important;
}

.action-label.rejected {
  font-weight: 600;
}

.status-divider {
  height: 20px;
  border-left: 1px solid #D5D5D8;
  position: absolute;
  transform: translateX(-50%);
  transition: left 0.3s ease;
}

.left-25 {
  left: 25%;
}

.left-50 {
  left: 50%;
}

.left-75 {
  left: 75%;
}

@media (max-width: 768px) {
  .card-container {
    padding-left: 6px !important;
    padding-right: 6px !important;
  }
}
</style>
